import cx from "classnames";
import { type ButtonHTMLAttributes, type ReactNode } from "react";

/**
 * Canonical height for EVERY Ask Puntt pane header.
 *
 * Every right-side pane (chat collapse strip, canvas, project status,
 * project-loading skeleton, ticket, asset viewer, artwork / context
 * columns) must render its title bar at this exact height so the column
 * titles line up at the same vertical y-coordinate no matter which
 * pane(s) the route happens to render. Import this constant instead of
 * hard-coding `h-12` so the contract lives in one place.
 */
export const PANE_HEADER_HEIGHT_CLASS = "h-12";

/** Shared fill for every column title bar (chat / artwork / context / …). */
export const PANE_HEADER_SURFACE_CLASS = "bg-puntt-neutral-gray-1";

/**
 * Hit-target + icon size for EVERY control in Ask Puntt pane headers
 * (close, collapse, back, prev/next, add-comment). Keep these in lockstep
 * so headers never mix a huge X with a tiny collapse glyph.
 */
export const PANE_HEADER_ICON_SIZE = 14;

/**
 * Size-7 header control chrome: white resting fill, grey hover only, no
 * outline. Size was unified across close/collapse/nav — coloring stays ghost.
 */
export const paneHeaderIconButtonClassName =
  "flex size-7 shrink-0 items-center justify-center rounded-md bg-base-white text-puntt-neutral-gray-11 transition-colors hover:bg-puntt-neutral-gray-3 hover:text-puntt-neutral-gray-12 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-base-white";

type PaneHeaderIconButtonProps = {
  "aria-label": string;
  "data-testid"?: string;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
} & Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "aria-label" | "children" | "className" | "disabled" | "onClick" | "type"
>;

/** Size-7 header control (close, collapse, prev/next) — no border outline. */
export function PaneHeaderIconButton({
  "aria-label": ariaLabel,
  "data-testid": dataTestId,
  onClick,
  disabled,
  className,
  children,
  ...rest
}: PaneHeaderIconButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={ariaLabel}
      data-testid={dataTestId}
      className={cx(paneHeaderIconButtonClassName, className)}
      {...rest}
    >
      {children}
    </button>
  );
}

export type PaneHeaderProps = {
  /** Region slug for `data-testid="pane-header-<region>"`. */
  region: string;
  title: ReactNode;
  /** Leading control (back, close, etc.). */
  leading?: ReactNode;
  /** Trailing controls (toolbar, collapse, etc.). */
  trailing?: ReactNode;
  className?: string;
};

/**
 * Shared column header for AskPuntt pane surfaces (artwork, context, ticket).
 * Standardizes height and testids across chat, project workspace, and ticket views.
 */
export function PaneHeader({
  region,
  title,
  leading,
  trailing,
  className,
}: PaneHeaderProps) {
  return (
    <div
      className={cx(
        PANE_HEADER_HEIGHT_CLASS,
        PANE_HEADER_SURFACE_CLASS,
        "flex min-w-0 shrink-0 items-center gap-2 border-b border-puntt-neutral-gray-6 px-4",
        className,
      )}
      data-testid={`pane-header-${region}`}
    >
      {leading}
      <h3 className="min-w-0 shrink-0 text-[11px] font-bold uppercase tracking-wide text-puntt-neutral-gray-11">
        {title}
      </h3>
      <div
        className="ml-auto flex shrink-0 items-center gap-2"
        data-testid={`pane-header-${region}-controls`}
      >
        {trailing}
      </div>
    </div>
  );
}
